"use client";

import { useState, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthModal from "@/app/homepage/components/AuthModal";
import NewsletterSection from "@/app/homepage/components/NewsletterSection";
import { useAuth } from "@/contexts/AuthContext";
import { createClient } from "@/lib/supabase/client";

const BODY_FONT = "'Nunito Sans', sans-serif";
const HEADING_FONT = "Rajdhani, sans-serif";

interface InvoiceFormData {
  fullName: string;
  companyName: string;
  companyAddress: string;
  orgNumber: string;
  email: string;
  phone: string;
}

interface Plan {
  id: string;
  name: string;
  price: string | null;
  unit: string | null;
  featured: boolean;
  tag: string | null;
  description: string;
  features: string[];
  notIncluded: string[];
  cta: string;
}

function InvoiceModal({
  plan,
  userEmail,
  onClose,
  onSuccess,
}: {
  plan: Plan;
  userEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState<InvoiceFormData>({
    fullName: "",
    companyName: "",
    companyAddress: "",
    orgNumber: "",
    email: userEmail,
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const today = new Date();
  const nextBilling = new Date(today);
  nextBilling.setMonth(nextBilling.getMonth() + 1);
  const nextBillingStr = nextBilling.toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Generate invoice number
      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 90000) + 10000).padStart(5, '0')}`;
      const today = new Date();
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + 14);
      const invoiceDateStr = today.toISOString().split('T')[0];
      const dueDateStr = dueDate.toISOString().split('T')[0];

      // Parse price amount
      const priceRaw = plan.price ? plan.price.replace(/\s/g, '') : '0';
      const amount = parseFloat(priceRaw) || 0;

      // Save to Supabase if user is logged in
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('invoices').insert({
          user_id: user.id,
          invoice_number: invoiceNumber,
          invoice_date: invoiceDateStr,
          plan_name: plan.name,
          amount,
          invoice_status: 'pending',
          due_date: dueDateStr,
          customer_name: form.fullName,
          company_name: form.companyName,
          org_number: form.orgNumber,
          address: form.companyAddress,
        });
      }

      // Generate and download PDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210;
      const margin = 20;

      // Header bar
      doc.setFillColor(10, 10, 10);
      doc.rect(0, 0, W, 40, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      doc.text('SEBNORGE', margin, 24);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(160, 160, 160);
      doc.text('Industrielle maskiner og løsninger', margin, 31);

      // Invoice title top right
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('FAKTURA', W - margin, 24, { align: 'right' });
      doc.setFontSize(10);
      doc.setTextColor(160, 160, 160);
      doc.text(`Nr: ${invoiceNumber}`, W - margin, 31, { align: 'right' });

      // Divider
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, 46, W - margin, 46);

      // Customer info
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text('FAKTURERT TIL', margin, 56);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(10, 10, 10);
      doc.setFontSize(10);
      doc.text(form.fullName, margin, 63);
      doc.text(form.companyName, margin, 69);
      doc.text(`Org.nr: ${form.orgNumber}`, margin, 75);
      doc.text(form.companyAddress, margin, 81);
      doc.text(form.email, margin, 87);
      doc.text(form.phone, margin, 93);

      // Invoice details right
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text('FAKTURADETALJER', W - margin, 56, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(10, 10, 10);
      doc.setFontSize(10);
      doc.text(`Fakturanr: ${invoiceNumber}`, W - margin, 63, { align: 'right' });
      doc.text(`Fakturadato: ${today.toLocaleDateString('nb-NO')}`, W - margin, 69, { align: 'right' });
      doc.text(`Forfallsdato: ${dueDate.toLocaleDateString('nb-NO')}`, W - margin, 75, { align: 'right' });
      doc.text('Status: Venter betaling', W - margin, 81, { align: 'right' });

      // Table header
      doc.setFillColor(10, 10, 10);
      doc.rect(margin, 104, W - margin * 2, 10, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('BESKRIVELSE', margin + 4, 111);
      doc.text('BELØP', W - margin - 4, 111, { align: 'right' });

      // Table row
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, 114, W - margin * 2, 12, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(10, 10, 10);
      doc.text(`${plan.name} — månedlig abonnement`, margin + 4, 122);
      doc.text(`NOK ${amount.toLocaleString('nb-NO', { minimumFractionDigits: 2 })}`, W - margin - 4, 122, { align: 'right' });

      // Total
      doc.setDrawColor(10, 10, 10);
      doc.line(margin, 132, W - margin, 132);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('TOTALT', margin, 142);
      doc.text(`NOK ${amount.toLocaleString('nb-NO', { minimumFractionDigits: 2 })}`, W - margin, 142, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text('MVA er inkludert i beløpet (25%)', margin, 150);

      // Bank info
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(10, 10, 10);
      doc.text('BETALINGSINFORMASJON', margin, 168);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text('Kontonummer: 1234.56.78901', margin, 175);
      doc.text('IBAN: NO12 1234 5678 901', margin, 181);
      doc.text('BIC/SWIFT: DNBANOKK', margin, 187);
      doc.text(`KID/Melding: ${invoiceNumber}`, margin, 193);

      // Footer
      doc.setFillColor(10, 10, 10);
      doc.rect(0, 270, W, 27, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(180, 180, 180);
      doc.text('SebNorge AS  |  kontakt@sebnorge.com  |  +47 22 83 83 10  |  www.sebnorge.com', W / 2, 280, { align: 'center' });
      doc.text('Org.nr: 123 456 789  |  Kontonr: 1234.56.78901  |  IBAN: NO12 1234 5678 901', W / 2, 287, { align: 'center' });

      doc.save(`faktura-${invoiceNumber}.pdf`);
    } catch (err) {
      console.error('Invoice error:', err);
    }

    setSubmitting(false);
    onSuccess();
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    backgroundColor: "#111111",
    border: "1px solid #2A2A2A",
    color: "white",
    fontFamily: BODY_FONT,
    fontWeight: 300,
    fontSize: "0.9rem",
    padding: "12px 14px",
    outline: "none",
    borderRadius: 0,
    boxSizing: "border-box" as const,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: HEADING_FONT,
    fontWeight: 600,
    fontSize: "0.62rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
    color: "rgba(255,255,255,0.4)",
    display: "block",
    marginBottom: "6px",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.85)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        overflowY: "auto",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: "#0A0A0A",
          border: "1px solid #2A2A2A",
          width: "100%",
          maxWidth: "520px",
          padding: "44px 40px",
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Close */}
        <button
          className="btn-press btn-press-dark"
          onClick={onClose}
          style={{
            position: "absolute",
            top: "18px",
            right: "20px",
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.35)",
            fontSize: "1.4rem",
            cursor: "pointer",
            lineHeight: 1,
            padding: "4px",
          }}
          aria-label="Lukk"
        >
          ×
        </button>

        {/* Title */}
        <p
          style={{
            fontFamily: HEADING_FONT,
            fontWeight: 600,
            fontSize: "0.62rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.3)",
            marginBottom: "10px",
          }}
        >
          Abonnement
        </p>
        <h2
          className="plan-title-mobile"
          style={{
            fontFamily: HEADING_FONT,
            fontWeight: 700,
            fontSize: "1.9rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "white",
            marginBottom: "32px",
            lineHeight: 1,
          }}
        >
          Oppdater din plan
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Full name */}
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>Fullt navn</label>
            <input
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              required
              placeholder="Ola Nordmann"
              style={inputStyle}
            />
          </div>

          {/* Company name */}
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>Firmanavn</label>
            <input
              name="companyName"
              value={form.companyName}
              onChange={handleChange}
              required
              placeholder="Bedriften AS"
              style={inputStyle}
            />
          </div>

          {/* Company address */}
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>Firmaadresse</label>
            <input
              name="companyAddress"
              value={form.companyAddress}
              onChange={handleChange}
              required
              placeholder="Storgata 1, 0182 Oslo"
              style={inputStyle}
            />
          </div>

          {/* Org number */}
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>Organisasjonsnummer</label>
            <input
              name="orgNumber"
              value={form.orgNumber}
              onChange={handleChange}
              required
              placeholder="123 456 789"
              style={inputStyle}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>E-postadresse</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              placeholder="din@epost.no"
              style={{ ...inputStyle, color: "rgba(255,255,255,0.6)" }}
            />
          </div>

          {/* Phone */}
          <div style={{ marginBottom: "32px" }}>
            <label style={labelStyle}>Telefonnummer for SMS-bekreftelse</label>
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              required
              placeholder="+47 900 00 000"
              style={inputStyle}
            />
          </div>

          {/* Plan summary */}
          <div
            style={{
              backgroundColor: "#111111",
              border: "1px solid #2A2A2A",
              padding: "22px 24px",
              marginBottom: "28px",
            }}
          >
            <p
              style={{
                fontFamily: HEADING_FONT,
                fontWeight: 600,
                fontSize: "0.6rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.3)",
                marginBottom: "16px",
              }}
            >
              Oppsummering
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "10px" }}>
              <span
                style={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "white",
                }}
              >
                {plan.name}
              </span>
              <span
                style={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 700,
                  fontSize: "1.3rem",
                  color: "white",
                  letterSpacing: "0.04em",
                }}
              >
                {plan.price} {plan.unit}
              </span>
            </div>
            <div
              style={{
                borderTop: "1px solid #2A2A2A",
                paddingTop: "12px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontFamily: BODY_FONT,
                  fontWeight: 300,
                  fontSize: "0.82rem",
                  color: "rgba(255,255,255,0.4)",
                }}
              >
                Neste fakturadato
              </span>
              <span
                style={{
                  fontFamily: BODY_FONT,
                  fontWeight: 300,
                  fontSize: "0.82rem",
                  color: "rgba(255,255,255,0.65)",
                }}
              >
                {nextBillingStr}
              </span>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="plan-cta-btn btn-press"
            style={{
              display: "block",
              width: "100%",
              backgroundColor: submitting ? "#E0E0E0" : "white",
              color: "#0A0A0A",
              border: "none",
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              fontSize: "0.82rem",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              padding: "16px",
              cursor: submitting ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              borderRadius: 0,
            }}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.backgroundColor = "#E0E0E0";
            }}
            onMouseLeave={(e) => {
              if (!submitting) e.currentTarget.style.backgroundColor = "white";
            }}
          >
            {submitting ? "Sender faktura..." : "Bekreft og betal med faktura →"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function PricingInteractive() {
  const { user, loading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "signup">("signup");
  const [selectedPlan, setSelectedPlan] = useState("Pro");
  const [invoiceModalPlan, setInvoiceModalPlan] = useState<Plan | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  const openSignupWithPlan = (plan: string) => {
    setSelectedPlan(plan);
    setAuthTab("signup");
    setAuthOpen(true);
  };

  const handlePlanCta = (plan: Plan) => {
    if (plan.id === "Enterprise") {
      window.location.href = "/homepage#kontakt";
      return;
    }
    if (!loading && user) {
      // User is logged in — open invoice modal directly
      setInvoiceModalPlan(plan);
    } else {
      // Not logged in — open signup modal
      openSignupWithPlan(plan.id);
    }
  };

  const handleInvoiceSuccess = () => {
    setInvoiceModalPlan(null);
    showToast(
      "Faktura lastet ned! Du vil også motta den på e-post og SMS."
    );
  };

  const openLogin = () => { setAuthTab("login"); setAuthOpen(true); };
  const openSignup = () => { setAuthTab("signup"); setAuthOpen(true); };

  const plans: Plan[] = [
    {
      id: "Gratis",
      name: "Gratis",
      price: "0",
      unit: "kr/mnd",
      featured: false,
      tag: null,
      description: "Kom i gang gratis. Utforsk plattformen uten kostnad – ingen kredittkort nødvendig.",
      features: [
        "Tilgang til 100 maskiner",
        "Grunnleggende søk og filter",
        "E-post support",
        "Eksport til PDF (5/mnd)",
      ],
      notIncluded: [
        "Sanntids oppdateringer",
        "API-tilgang",
        "Prioritert support",
        "Avansert analytics",
      ],
      cta: "Start gratis",
    },
    {
      id: "Basis",
      name: "Basis",
      price: "490",
      unit: "kr/mnd",
      featured: false,
      tag: null,
      description: "Perfekt for enkeltoperatører og små bedrifter som starter med SebNorge.",
      features: [
        "Tilgang til 1 000 maskiner",
        "Programvareoppdateringer",
        "E-post support",
        "Basis analytics",
        "Eksport til PDF",
      ],
      notIncluded: [
        "Sanntids oppdateringer",
        "API-tilgang",
        "Prioritert support",
        "Dedikert rådgiver",
      ],
      cta: "Kom i gang",
    },
    {
      id: "Pro",
      name: "Pro",
      price: "1 490",
      unit: "kr/mnd",
      featured: true,
      tag: "Mest populær",
      description: "For voksende bedrifter som trenger full tilgang og sanntids integrasjon.",
      features: [
        "Alle 15 000 maskiner",
        "Sanntids oppdateringer",
        "Prioritert support 24/7",
        "Full API-tilgang",
        "Avansert analytics",
        "Teamtilgang (5 brukere)",
        "Tilpassede varsler",
        "Eksport til alle formater",
      ],
      notIncluded: [
        "Dedikert kundeansvarlig",
        "Skreddersydde løsninger",
      ],
      cta: "Start Pro-prøveperiode",
    },
    {
      id: "Enterprise",
      name: "Enterprise",
      price: null,
      unit: null,
      featured: false,
      tag: null,
      description: "Skreddersydde løsninger for store organisasjoner med komplekse behov.",
      features: [
        "Alt i Pro",
        "Dedikert kundeansvarlig",
        "Skreddersydde integrasjoner",
        "SLA-garantier",
        "On-premise alternativ",
        "Ubegrenset brukere",
        "Prioritert onboarding",
        "Kvartalsvise gjennomganger",
      ],
      notIncluded: [],
      cta: "Kontakt oss",
    },
  ];

  const faqs = [
    {
      q: "Kan jeg bytte plan når som helst?",
      a: "Ja. Du kan oppgradere eller nedgradere planen din når som helst fra kontopanelet ditt. Endringer trer i kraft umiddelbart, og du faktureres kun for differansen.",
    },
    {
      q: "Hva skjer etter prøveperioden?",
      a: "Etter 14 dagers gratis prøveperiode faktureres du automatisk for valgt plan. Du kan kansellere når som helst før prøveperioden utløper uten kostnad.",
    },
    {
      q: "Støtter dere faktura og EHF?",
      a: "Ja. Vi støtter faktura, EHF-fakturering og kortbetaling. Enterprise-kunder kan forhandle om egne betalingsbetingelser med 30 eller 60 dagers forfall.",
    },
    {
      q: "Hva er inkludert i API-tilgangen?",
      a: "Pro og Enterprise-kunder får full REST API-tilgang med dokumentasjon, SDK for Python og JavaScript, webhooks og ubegrenset API-kall innenfor fair use.",
    },
    {
      q: "Kan jeg legge til flere brukere på Pro-planen?",
      a: "Pro-planen inkluderer 5 brukere. Du kan legge til ekstra brukere for 299 kr/mnd per bruker. Enterprise-planen inkluderer ubegrenset brukere.",
    },
    {
      q: "Hva betyr dedikert kundeansvarlig i Enterprise?",
      a: "Du får en navngitt kontaktperson hos SebNorge som kjenner din bedrift og maskinpark. Inkluderer kvartalsvise gjennomganger og proaktiv oppfølging.",
    },
  ];

  return (
    <>
      <Header onLoginClick={openLogin} onSignupClick={openSignup} transparent={false} />

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "32px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#111111",
            border: "1px solid #2A2A2A",
            color: "white",
            fontFamily: BODY_FONT,
            fontWeight: 300,
            fontSize: "0.9rem",
            padding: "14px 28px",
            zIndex: 99999,
            whiteSpace: "nowrap",
            letterSpacing: "0.01em",
            lineHeight: 1.5,
          }}
        >
          {toast}
        </div>
      )}

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        defaultPlan={selectedPlan}
        showToast={showToast}
      />

      {invoiceModalPlan && (
        <InvoiceModal
          plan={invoiceModalPlan}
          userEmail={user?.email ?? ""}
          onClose={() => setInvoiceModalPlan(null)}
          onSuccess={handleInvoiceSuccess}
        />
      )}

      <main style={{ backgroundColor: "#0A0A0A", paddingTop: "100px" }}>

        {/* ── Page Header ── */}
        <section style={{ padding: "80px 0 100px", borderBottom: "1px solid #2A2A2A" }}>
          <div className="max-w-5xl mx-auto px-6 text-center">
            <p className="section-label" style={{ marginBottom: "16px" }}>Abonnement</p>
            <h1
              style={{
                fontFamily: HEADING_FONT,
                fontWeight: 700,
                fontSize: "clamp(2.5rem, 6vw, 5rem)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "white",
                lineHeight: 0.95,
                marginBottom: "24px",
              }}
            >
              Velg din plan
            </h1>
            <p
              style={{
                fontFamily: BODY_FONT,
                fontWeight: 400,
                fontSize: "1rem",
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.75,
                maxWidth: "480px",
                margin: "0 auto",
              }}
            >
              Få tilgang til hele SebNorge-plattformen. Alle planer inkluderer fri kansellering og 30 dagers pengene-tilbake-garanti.
            </p>
          </div>
        </section>

        {/* ── Plans Grid ── */}
        <section style={{ padding: "80px 0 100px" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div
              className="plans-grid"
              style={{
                border: "1px solid #2A2A2A",
              }}
            >
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="plan-grid-item"
                  style={{
                    padding: "44px 32px",
                    backgroundColor: plan.featured ? "#111111" : "transparent",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Featured tag */}
                  {plan.tag && (
                    <div
                      style={{
                        position: "absolute",
                        top: "0",
                        left: "32px",
                        backgroundColor: "white",
                        color: "#0A0A0A",
                        fontFamily: HEADING_FONT,
                        fontWeight: 700,
                        fontSize: "0.62rem",
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        padding: "4px 12px",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {plan.tag}
                    </div>
                  )}

                  <p className="section-label" style={{ marginBottom: "10px" }}>Plan</p>
                  <h2
                    className="plan-title-mobile"
                    style={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 700,
                      fontSize: "1.9rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "white",
                      marginBottom: "14px",
                    }}
                  >
                    {plan.name}
                  </h2>

                  {/* Price */}
                  <div style={{ marginBottom: "20px" }}>
                    {plan.price ? (
                      <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                        <span
                          style={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 700,
                            fontSize: "2.6rem",
                            color: "white",
                            letterSpacing: "0.02em",
                            lineHeight: 1,
                          }}
                        >
                          {plan.price}
                        </span>
                        <span
                          style={{
                            fontFamily: BODY_FONT,
                            fontWeight: 400,
                            fontSize: "0.8rem",
                            color: "rgba(255,255,255,0.4)",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {plan.unit}
                        </span>
                      </div>
                    ) : (
                      <p
                        style={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 700,
                          fontSize: "1.8rem",
                          color: "white",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}
                      >
                        Kontakt oss
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      fontFamily: BODY_FONT,
                      fontWeight: 400,
                      fontSize: "0.875rem",
                      color: "rgba(255,255,255,0.5)",
                      lineHeight: 1.7,
                      marginBottom: "28px",
                      paddingBottom: "28px",
                      borderBottom: "1px solid #2A2A2A",
                    }}
                  >
                    {plan.description}
                  </p>

                  {/* Features */}
                  <div style={{ flex: 1, marginBottom: "36px" }}>
                    <p className="section-label" style={{ marginBottom: "14px" }}>Inkludert</p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        marginBottom: plan.notIncluded.length > 0 ? "20px" : "0",
                      }}
                    >
                      {plan.features.map((feature) => (
                        <div key={feature} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                          <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.65rem", marginTop: "4px", flexShrink: 0 }}>—</span>
                          <span
                            style={{
                              fontFamily: BODY_FONT,
                              fontWeight: 400,
                              fontSize: "0.85rem",
                              color: "rgba(255,255,255,0.72)",
                              lineHeight: 1.5,
                            }}
                          >
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {plan.notIncluded.length > 0 && (
                      <>
                        <p
                          className="section-label"
                          style={{ marginBottom: "10px", marginTop: "14px", color: "rgba(255,255,255,0.2)" }}
                        >
                          Ikke inkludert
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {plan.notIncluded.map((feature) => (
                            <div key={feature} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                              <span
                                style={{
                                  color: "rgba(255,255,255,0.18)",
                                  fontSize: "0.65rem",
                                  marginTop: "4px",
                                  flexShrink: 0,
                                }}
                              >
                                —
                              </span>
                              <span
                                style={{
                                  fontFamily: BODY_FONT,
                                  fontWeight: 400,
                                  fontSize: "0.85rem",
                                  color: "rgba(255,255,255,0.22)",
                                  lineHeight: 1.5,
                                }}
                              >
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => handlePlanCta(plan)}
                    className="plan-cta-btn btn-press"
                    style={{
                      display: "block",
                      width: "100%",
                      backgroundColor: plan.featured ? "white" : "transparent",
                      color: plan.featured ? "#0A0A0A" : "white",
                      border: plan.featured ? "none" : "1px solid rgba(255,255,255,0.3)",
                      fontFamily: HEADING_FONT,
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      padding: "15px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget;
                      if (plan.featured) {
                        el.style.backgroundColor = "#F4F4F4";
                      } else {
                        el.style.backgroundColor = "rgba(255,255,255,0.06)";
                        el.style.borderColor = "rgba(255,255,255,0.6)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget;
                      if (plan.featured) {
                        el.style.backgroundColor = "white";
                      } else {
                        el.style.backgroundColor = "transparent";
                        el.style.borderColor = "rgba(255,255,255,0.3)";
                      }
                    }}
                  >
                    {!loading && user && plan.id !== "Enterprise" ?"Oppdater plan"
                      : plan.cta}
                  </button>
                </div>
              ))}
            </div>

            {/* Billing note */}
            <p
              style={{
                fontFamily: BODY_FONT,
                fontWeight: 400,
                fontSize: "0.8rem",
                color: "rgba(255,255,255,0.25)",
                textAlign: "center",
                marginTop: "24px",
                letterSpacing: "0.01em",
              }}
            >
              Alle priser er ekskl. MVA · Ingen bindingstid · 14 dager gratis prøveperiode · 30 dagers pengene-tilbake-garanti
            </p>
          </div>
        </section>

        {/* ── Trust Badges ── */}
        <section
          style={{
            padding: "100px 0",
            borderTop: "1px solid #2A2A2A",
            borderBottom: "1px solid #2A2A2A",
          }}
        >
          <div className="max-w-5xl mx-auto px-6">
            <p className="section-label" style={{ textAlign: "center", marginBottom: "48px" }}>
              Sikkerhet og garanti
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "0",
                border: "1px solid #2A2A2A",
              }}
              className="trust-badges-grid"
            >
              {[
                {
                  label: "256-bit kryptering",
                  title: "SSL Sikker tilkobling",
                  desc: "Alle data krypteres med TLS 1.3. Din informasjon er alltid beskyttet under overføring og lagring på SebNorge-plattformen.",
                },
                {
                  label: "Norsk personvernlov",
                  title: "GDPR Personvern",
                  desc: "Fullstendig GDPR-samsvar. Du eier dine data og kan slette dem når som helst etter norsk og europeisk personvernlovgivning.",
                },
                {
                  label: "SLA-garantert",
                  title: "99% Oppetidsgaranti",
                  desc: "Vi garanterer 99% oppetid på hele plattformen. Overvåket 24/7 med automatisk feilgjenoppretting og transparent statussider.",
                },
              ].map((badge, i) => (
                <div
                  key={badge.title}
                  className="trust-badge-item"
                  style={{
                    padding: "48px 40px",
                    borderRight: i < 2 ? "1px solid #2A2A2A" : "none",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                      fontSize: "0.62rem",
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.28)",
                      marginBottom: "14px",
                    }}
                  >
                    {badge.label}
                  </p>
                  <h3
                    style={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 700,
                      fontSize: "1.25rem",
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: "white",
                      marginBottom: "16px",
                      lineHeight: 1.1,
                    }}
                  >
                    {badge.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: BODY_FONT,
                      fontWeight: 400,
                      fontSize: "0.875rem",
                      color: "rgba(255,255,255,0.42)",
                      lineHeight: 1.75,
                    }}
                  >
                    {badge.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section style={{ padding: "100px 0", borderBottom: "1px solid #2A2A2A" }}>
          <div className="max-w-3xl mx-auto px-6">
            <p className="section-label" style={{ textAlign: "center", marginBottom: "16px" }}>Spørsmål og svar</p>
            <h2
              style={{
                fontFamily: HEADING_FONT,
                fontWeight: 700,
                fontSize: "clamp(1.8rem, 4vw, 3rem)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "white",
                textAlign: "center",
                marginBottom: "64px",
                lineHeight: 1,
              }}
            >
              Vanlige spørsmål
            </h2>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  style={{
                    borderTop: "1px solid #2A2A2A",
                    padding: "32px 0",
                    borderBottom: i === faqs.length - 1 ? "1px solid #2A2A2A" : "none",
                  }}
                >
                  <p
                    style={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 600,
                      fontSize: "1.05rem",
                      letterSpacing: "0.04em",
                      color: "white",
                      marginBottom: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {faq.q}
                  </p>
                  <p
                    style={{
                      fontFamily: BODY_FONT,
                      fontWeight: 400,
                      fontSize: "0.9rem",
                      color: "rgba(255,255,255,0.5)",
                      lineHeight: 1.75,
                    }}
                  >
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <NewsletterSection />
      </main>

      <Footer />
    </>
  );
}